/** Centralized TanStack Query keys so invalidation stays consistent. */
import type { MetricsRange } from './index';
export const qk = {
  me: ['me'] as const,
  // Cross-workspace dashboard feeds (aggregate across all the caller's workspaces).
  myOverview: ['me', 'overview'] as const,
  myProjects: ['me', 'projects'] as const,
  myPrototypes: ['me', 'prototypes'] as const,
  myGrants: (orgId: string | null | undefined) => ['me', 'grants', orgId ?? null] as const,
  projects: ['projects'] as const,
  project: (id: string) => ['project', id] as const,
  /** Owner-only native end-user feedback status (repo projects). */
  projectFeedback: (id: string) => ['projectFeedback', id] as const,
  projectPullRequests: (id: string) => ['projectPullRequests', id] as const,
  /** Keyed on the shipped count too: a new ship refetches the star details (the head names the new issue). */
  constellation: (id: string, shippedCount: number) => ['constellation', id, shippedCount] as const,
  projectIssues: (id: string) => ['project', id, 'issues'] as const,
  projectIssuesBoard: (
    id: string,
    params: { status: string; tag: string; q: string; page: number },
  ) =>
    [
      'project',
      id,
      'issues',
      'board',
      params.status,
      params.tag,
      params.q,
      params.page,
    ] as const,
  /** First board page (default order) the issue page reads for its previous/next buttons. */
  projectIssueNeighbours: (id: string) => ['project', id, 'issues', 'neighbours'] as const,
  projectLoops: (id: string) => ['project', id, 'loops'] as const,
  projectGrants: (id: string) => ['project', id, 'grants'] as const,
  // Root chat key: invalidating this refreshes the session list AND every
  // session's messages (both keys extend it).
  chat: (projectId: string) => ['project', projectId, 'chat'] as const,
  chatSessions: (projectId: string) => ['project', projectId, 'chat', 'sessions'] as const,
  chatMessages: (projectId: string, sessionId: string) =>
    ['project', projectId, 'chat', 'messages', sessionId] as const,
  prototypes: ['prototypes'] as const,
  prototype: (id: string) => ['prototype', id] as const,
  prototypePulls: (id: string) => ['prototype', id, 'pulls'] as const,
  prototypeStats: (id: string) => ['prototype', id, 'stats'] as const,
  prototypeInspect: (id: string) => ['prototype', id, 'inspect'] as const,
  prototypeSchema: (id: string) => ['prototype', id, 'schema'] as const,
  prototypeApiSpec: (id: string) => ['prototype', id, 'api-spec'] as const,
  prototypeBuildLog: (id: string) => ['prototype', id, 'build-log'] as const,
  prototypeSecrets: (id: string) => ['prototype', id, 'secrets'] as const,
  prototypeAdminAccount: (id: string) => ['prototype', id, 'admin-account'] as const,
  issue: (id: string) => ['issue', id] as const,
  issueRuns: (id: string) => ['issue', id, 'runs'] as const,
  issueTimeline: (id: string) => ['issue', id, 'timeline'] as const,
  issueComments: (id: string) => ['issue', id, 'comments'] as const,
  issueAttachments: (id: string) => ['issue', id, 'attachments'] as const,
  run: (id: string) => ['run', id] as const,
  runLogs: (id: string) => ['run', id, 'logs'] as const,
  runAttempts: (id: string) => ['run', id, 'attempts'] as const,
  activeRuns: ['runs', 'active'] as const,
  recentRuns: ['runs', 'recent'] as const,
  metrics: (range: MetricsRange, orgId: string | null | undefined) =>
    ['metrics', orgId ?? null, range] as const,
  settings: ['settings'] as const,
  mcpConnections: ['mcp-connections'] as const,
  integrationTokens: (projectId: string | null) => ['integration-tokens', projectId ?? 'workspace'] as const,
  integrationWebhookDeliveries: (projectId: string | null, tokenId: string) =>
    ['integration-tokens', projectId ?? 'workspace', tokenId, 'deliveries'] as const,
  version: ['version'] as const,
  githubRepos: ['github', 'repos'] as const,
  loops: ['loops'] as const,
  loop: (id: string) => ['loop', id] as const,
  loopHealth: (id: string) => ['loop', id, 'health'] as const,
  workspaceFiles: (runId: string) => ['run', runId, 'workspace'] as const,
  workspaceFile: (runId: string, path: string) =>
    ['run', runId, 'workspace', 'file', path] as const,
  workspaceBaseFile: (runId: string, path: string) =>
    ['run', runId, 'workspace', 'base', path] as const,
  logSearch: (q: string) => ['runs', 'search', 'logs', q] as const,
  orgMembers: (orgId: string) => ['org', orgId, 'members'] as const,
  /** Root of every transfer list (invalidate after any transfer action). */
  transfers: ['transfers'] as const,
  transferDestinations: (projectId: string) => ['transfers', 'destinations', projectId] as const,
  outgoingTransfers: (orgId: string | null | undefined) => ['transfers', 'outgoing', orgId ?? null] as const,
  incomingTransfers: (orgSlug: string) => ['transfers', 'incoming', orgSlug] as const,
};
